import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DownloadButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Flex,
  ListItem,
  Text,
} from '@/components/ui';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { useDownloadFile } from '@/hooks/useDownloadFile';
import { PERMISSIONS } from '@/types/permissions';
import { getCsvFileDownloadEndpoint } from '@/utils/downloadEndpoints';
import {
  IconDots,
  IconDownload,
  IconEye,
  IconHistory,
  IconLoader,
  IconTrash,
} from '@tabler/icons-react';
import { CsvFileActionsProps } from './types';

const CsvFileActions: React.FC<CsvFileActionsProps> = ({
  file,
  onDelete,
  onShowVersions,
  onPreview,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const canDeleteCSV = usePermissionAccess([PERMISSIONS.CSV_DELETE]);
  // Note: All files in the list can be previewed/downloaded/versioned
  // Backend controls what files are returned in the list

  const { downloadFromApi, isLoading: isDownloading } = useDownloadFile({
    showToast: true,
    successMessage: `Downloaded ${file.filename}`,
    errorMessage: 'Failed to download file',
  });

  const handleDeleteConfirm = () => {
    onDelete?.(file);
    setDeleteDialogOpen(false);
  };

  const handlePreview = () => {
    onPreview?.(file);
  };

  const handleShowVersions = () => {
    onShowVersions?.(file);
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    await downloadFromApi(getCsvFileDownloadEndpoint(file), file.filename);
  };

  return (
    <Flex gap="1" align="center">
      {/* Download button */}
      <DownloadButton
        apiEndpoint={getCsvFileDownloadEndpoint(file)}
        filename={file.filename}
        variant="text"
        size="small"
        isIconOnly
        title="Download file"
        successMessage={`Downloaded ${file.filename}`}
        errorMessage="Failed to download file"
      />

      {/* Preview button - available for all files in the list */}
      {onPreview && (
        <Button
          variant="text"
          size="small"
          isIconOnly
          onClick={handlePreview}
          startIcon={<IconEye />}
          title="Preview file"
        />
      )}

      {/* Versions history button - available for all files in the list */}
      {onShowVersions && (
        <Button
          variant="text"
          size="small"
          isIconOnly
          onClick={handleShowVersions}
          startIcon={<IconHistory />}
          title="View version history"
        />
      )}

      {/* Delete button with confirmation dialog - disabled if no permission */}
      {canDeleteCSV && onDelete ? (
        <Dialog.Root open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <Dialog.Trigger>
            <Button
              variant="text"
              size="small"
              color="error"
              isIconOnly
              startIcon={<IconTrash />}
              title="Delete file"
            />
          </Dialog.Trigger>
          <Dialog.Content size="medium">
            <Dialog.Header title="Delete CSV File" />
            <Dialog.Body>
              <Text size="2">
                Are you sure you want to delete{' '}
                <strong>"{file.filename}"</strong>? This action cannot be
                undone.
              </Text>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.Close>
                <Button variant="outlined" color="secondary">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                variant="classic"
                color="error"
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Root>
      ) : (
        <Button
          variant="text"
          size="small"
          color="error"
          isIconOnly
          startIcon={<IconTrash />}
          title="You do not have permission to delete files"
          disabled={true}
        />
      )}

      {/* More actions dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            variant="text"
            size="small"
            isIconOnly
            startIcon={<IconDots />}
            title="More actions"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent variant="soft" color="gray">
          <DropdownMenuItem asChild>
            <ListItem
              startIcon={
                isDownloading ? (
                  <IconLoader className="animate-spin" />
                ) : (
                  <IconDownload />
                )
              }
              primary="Download"
              onClick={handleDownload}
              disabled={isDownloading}
            />
          </DropdownMenuItem>
          {onPreview && (
            <DropdownMenuItem asChild>
              <ListItem
                startIcon={<IconEye />}
                primary="Preview File"
                onClick={handlePreview}
              />
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <ListItem
              startIcon={<IconHistory />}
              primary="Version History"
              onClick={handleShowVersions}
            />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <ListItem
              startIcon={<IconTrash />}
              primary="Delete"
              onClick={() => {
                if (canDeleteCSV && onDelete) {
                  setDeleteDialogOpen(true);
                }
              }}
              className="text-red-600"
              disabled={!canDeleteCSV || !onDelete}
              title={
                !canDeleteCSV || !onDelete
                  ? 'You do not have permission to delete files'
                  : undefined
              }
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Flex>
  );
};

export default CsvFileActions;
